import { nextTick } from 'vue'
import { useEditorStore } from '@/stores/editor.ts'
import { useTable } from '@/views/editor/composable/table/useTable.ts'
import { checkBlock, deletePrefix, setDataType, socketSave, eventDefault, addPrefix, newBlockEvent } from '@/views/editor/composable/useNote.ts'
import { storeToRefs } from 'pinia'
import { useContextMenu } from '@/views/editor/composable/table/useContextMenu.ts'
import { useDeleteTable } from '@/views/editor/composable/table/useDeleteTable.ts'
import { useSize } from '@/views/editor/composable/table/useSize.ts'
import { useCommonStore } from '@/stores/common.ts'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { useSelectorOverlay } from '@/views/editor/composable/table/selector/selctorOverlay.ts'
import { sanitizeCellContent, stripFunctionCloseButton } from '@/composables/common.ts'

function getColumnLabel(index: number): string {
	let label = ''
	let num = index

	while (num > 0) {
		const remainder = (num - 1) % 26
		label = String.fromCharCode(65 + remainder) + label
		num = Math.floor((num - 1) / 26)
	}

	return label
}

const usedTable = useTable()

let functionListClickHandler: (() => void) | null = null
let globalClickHandlerAttached = false
let globalClickHandler: ((event: Event) => void) | null = null
let tableInstanceCount = 0

const eventsAttachedTables = new WeakSet<HTMLElement>()

export function useCreateTable() {
	async function table() {
		try {
			const history = new UseHistory()
			const { selector, noteType, oldBlockUid, newBlockId } = storeToRefs(useEditorStore())

			if (!selector.value?.id && !newBlockId.value) {
				return
			}

			let blockUid = newBlockId.value || deletePrefix(selector.value.id)
			if (!checkBlock()) {
				const { isWrite } = storeToRefs(useEditorStore())
				const tempId = `temp-${useCommonStore().uniqueId()}`
				oldBlockUid.value = tempId

				const containerDiv = document.createElement('div')
				containerDiv.classList.add('editor--block__container')
				containerDiv.contentEditable = 'false'

				const newDiv = document.createElement('div')
				newDiv.id = tempId
				newDiv.classList.add('editor--block')
				newDiv.dataset.type = 'text'
				newDiv.setAttribute('contenteditable', 'true')
				newDiv.setAttribute('tabIndex', '1')
				containerDiv.appendChild(newDiv)

				const currentContainer = selector.value.closest('.editor--block__container')
				if (currentContainer) {
					currentContainer.insertAdjacentElement('afterend', containerDiv)
				}

				selector.value = newDiv
				blockUid = tempId
			}

			const { tableBlockTemp } = storeToRefs(useEditorStore())

			const table = insertTable(tableBlockTemp.value.content, blockUid)
			const blockId = addPrefix(blockUid)
			let block: any = document.getElementById(blockId) as HTMLElement

			// blockId로 블록을 찾지 못했으면 selector를 사용
			if (!block && selector.value) {
				block = selector.value
			}

			if (!block) {
				return
			}

			const newBlock: HTMLDivElement | any = document.createElement('div')
			newBlock.classList.add('editor--block')
			newBlock.setAttribute('contenteditable', 'false')
			newBlock.setAttribute('tabIndex', '0')
			newBlock.id = block.id
			newBlock.dataset.type = 'defaultTable'
			newBlock.classList.remove('active')
			newBlock.contentEditable = 'false'
			newBlock.innerHTML = table
			block.insertAdjacentElement('beforebegin', newBlock)
			block.remove()

			selector.value = newBlock

			event(newBlock)

			// 테이블 블록에 copy/paste 이벤트 리스너 등록
			newBlockEvent()

			const createdTable = newBlock.querySelector('table')
			if (createdTable) {
				const createTable = useCreateTable()
				createTable.tableShapes(createdTable)
				createTable.selector()
				createTable.widthSizeItem(createdTable)
				createTable.heightSizeItem(createdTable)
			}
			// md 규칙: 테이블 추가도 undo 저장
			history.push()
		} catch (error) {}
	}

	function insertTable(object: any, blockUid: string): string {
		const { isWrite, blockList } = storeToRefs(useEditorStore())
		const innerTableTestId = 'editor-block-inner-table-cell-'
		const rowNumberlineTestId = 'editor-block-row-number-line-'
		const columnNumberlineTestId = 'editor-block-column-number-line-'
		if (typeof object === 'string' || !object) return ''
		let tableContainer = `<div class="editor--block__table-container"><table tabIndex="0" id="prefix-${object.tableUID}" data-testid="editor-table-container">`

		if (!object.cell && object.rows) {
			object.cell = object.rows
		}
		object.rows = null

		object.cell?.forEach((row: any, idx: number) => {
			const cellContents = row.map((cell: any) => cell.content?.substring(0, 10) || 'EMPTY').join(', ')
		})

		let tableCol: number = 0
		let rows: any[] = []
		let tmpRows: any = []
		let tmpRowsCount: number = 0

		if (object.cell) {
			tmpRows = [...object.cell[0]]
			tmpRows.forEach((item: any) => {
				if (item && item.colSpan && item.colSpan > 1) {
					tableCol += item.colSpan
				} else {
					tableCol++
				}
			})
			rows = [...object.cell]

			if (tableCol > 100) {
				tableCol = Math.min(tableCol, 100)
			}
		}

		let width: any = []
		let height: any = []
		const totalRowCount = rows.length
		rows.forEach((row: any, rowIndex: number) => {
			if (row) {
				row.forEach((cells: any, cellIndex: number) => {
					if (rowIndex === 0 && cellIndex === 0) {
						if (cells.dataType && cells.dataType.size) {
							const allSize = typeof cells.dataType.size === 'string' ? JSON.parse(cells.dataType.size) : cells.dataType.size
							width = allSize.width
							height = allSize.height
						} else {
							width = Array(tableCol).fill(120)
							height = Array(totalRowCount).fill(40)
						}
					}
				})
			}
		})
		tableContainer += `<tr>`

		for (let i: number = 0; i <= tableCol; i++) {
			if (i === 0) {
				tableContainer += `<td class="number--line" style="width: 20px;" data-testid="${rowNumberlineTestId + i}"></td>`
			} else {
				tableContainer += `<td class="number--line" style="width: ${width[i - 1] || 120}px; height: 20px;" data-testid="${rowNumberlineTestId + i}">${getColumnLabel(i)}</td>`
			}
		}
		tableContainer += `</tr>`

		rows.forEach((rows: any, rowIndex: number) => {
			tableContainer += `<tr>`
			if (rows && rows.length > 0) {
				rows.forEach((cells: any, cellIndex: number) => {
					if (cellIndex === 0) {
						tableContainer += `<td class="number--line" style="height: ${height[rowIndex]}px; box-sizing: content-box;" data-testid="${columnNumberlineTestId + rowIndex}">${String(rowIndex + 1)}</td>`
					}
					if (!cells.dataType) cells.dataType = { colSpan: cells.colSpan || 1, rowSpan: cells.rowSpan || 1 }
					let dataTypeString = ''
					Object.entries(cells.dataType).forEach(([key, value]) => {
						if (key !== 'colSpan' && key !== 'rowSpan' && key !== 'width' && key !== 'height') {
							const escaped = String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;')
							dataTypeString += ` data-${key}="${escaped}"`
						}
					})
					const cellWidth = width[cellIndex] || 120
					if (cells.dataType.type === 'function') {
						const functionCellContent = stripFunctionCloseButton(sanitizeCellContent(cells.content))
						tableContainer += `<td id="prefix-${cells.cellUID || cells.cellUid}" data-testid="${innerTableTestId + rowIndex + cellIndex}" ${dataTypeString} colSpan="${cells.colSpan || cells.dataType?.colSpan || 1}" rowSpan="${cells.rowSpan || cells.dataType?.rowSpan || 1}" style="width: ${cellWidth}px"><div contenteditable="false"><span>${functionCellContent}</span><i class="ico ico--table__close" data-testid="table-close-btn"></i></div></td>`
					} else if (cells.dataType.type === 'error') {
						console.log(cells)
						const errorCellContent = stripFunctionCloseButton(sanitizeCellContent(cells.content)).replace(/<[^>]*>/g, '')
						let errorHeightTmp = height[rowIndex] || 40
						const errorRowSpan = cells.rowSpan || 1
						if (errorRowSpan > 1) {
							for (let i = 1; i < errorRowSpan; i++) {
								errorHeightTmp += height[rowIndex + i] || 40
							}
						}
						tableContainer += `<td id="prefix-${cells.cellUID || cells.cellUid}" data-testid="${innerTableTestId + rowIndex + cellIndex}" ${dataTypeString} colSpan="${cells.colSpan || cells.dataType?.colSpan || 1}" rowSpan="${cells.rowSpan || cells.dataType?.rowSpan || 1}" style="width: ${cellWidth}px"><div contenteditable="false" style="height: ${errorHeightTmp}px"><span>${errorCellContent}</span><i class="ico ico--table__close" data-testid="table-close-btn"></i></div></td>`
					} else if (cells.dataType.type === 'dropbox' || cells.dataType.type === 'schemaDropbox') {
						const dropboxName = cells.dataType.dropboxName ? cells.dataType.dropboxName.split(',') : cells.dataType.dropboxname.split(',')
						const dropboxCode = cells.dataType.dropboxCode ? cells.dataType.dropboxCode.split(',') : cells.dataType.dropboxcode.split(',')
						const dropboxSelect = cells.dataType.dropboxSelect !== undefined ? cells.dataType.dropboxSelect : cells.dataType.dropboxselect
						const isSelected = dropboxSelect !== undefined && dropboxSelect !== null && dropboxSelect !== '' && dropboxSelect !== 'undefined' && dropboxSelect !== 'null'
						let dropboxValue = dropboxName[0]

						if (isSelected) {
							dropboxCode.forEach((code: any, index: number) => {
								if (String(dropboxSelect) === String(code)) {
									dropboxValue = dropboxName[index]
								}
							})
						}
						let dropbox = `<div class="table--dropbox">`
						dropbox += `<div class="select--custom">`
						dropbox += `<div class="select--value">`
						dropbox += `<p>${dropboxValue}</p>`
						dropbox += `<i class="ico ico--select__arrow"></i>`
						dropbox += `</div>`
						dropbox += `</div>`
						dropbox += `<i class="ico ico--table__close" data-testid="table-close-btn"></i>`
						dropbox += `</div>`

						tableContainer += `<td id="prefix-${cells.cellUID || cells.cellUid}" data-testid="${innerTableTestId + rowIndex + cellIndex}" ${dataTypeString} colSpan="${cells.colSpan || 1}" rowSpan="${cells.rowSpan || 1}" style="width: ${cellWidth}px">${dropbox}</td>`
					} else if (cells.dataType.type === 'pdf') {
						const pdfName = cells.dataType.pdfName || cells.dataType.pdfname || 'file.pdf'
						tableContainer += `<td id="prefix-${cells.cellUID || cells.cellUid}" data-testid="${innerTableTestId + rowIndex + cellIndex}" ${dataTypeString} colSpan="${cells.colSpan || 1}" rowSpan="${cells.rowSpan || 1}" style="width: ${cellWidth}px"><div contenteditable="false" style="width: 100%; height: 100%; position: relative; display: flex; align-items: center; gap: 6px; padding: 0 8px; cursor: pointer;"><i class="ico ico--file__pdf" style="flex-shrink: 0; width: 20px; height: 20px;"></i><span class="table--pdf__name" style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; color: #181e26;">${pdfName}</span><i class="ico ico--table__close" style="flex-shrink: 0; z-index: 1;" data-testid="table-close-btn"></i></div></td>`
					} else if (cells.dataType.type === 'image') {
						let imageHeightTmp = height[rowIndex] || 40
						const imageRowSpan = cells.rowSpan || 1
						if (imageRowSpan > 1) {
							for (let i = 1; i < imageRowSpan; i++) {
								imageHeightTmp += height[rowIndex + i] || 40
							}
						}
						let imageCellContent = stripFunctionCloseButton(sanitizeCellContent(cells.content))
						imageCellContent = imageCellContent.replace(/<img([^>]*)\s*style="[^"]*"/, '<img$1 style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"')
						tableContainer += `<td id="prefix-${cells.cellUID || cells.cellUid}" data-testid="${innerTableTestId + rowIndex + cellIndex}" ${dataTypeString} colSpan="${cells.colSpan || 1}" rowSpan="${cells.rowSpan || 1}" style="width: ${cellWidth}px; height: ${imageHeightTmp}px;"><div contenteditable="false" style="width: 100%; height: 100%; position: relative; overflow: hidden;">${imageCellContent}<i class="ico ico--table__close" style="position: absolute; top: 2px; right: 2px; z-index: 1;" data-testid="table-close-btn"></i></div></td>`
					} else {
						if ((cells.dataType.schemaUid || cells.dataType.schemauid) && cells.dataType.header === 'true') {
							tableContainer += `<td id="prefix-${cells.cellUID || cells.cellUid}" data-testid="${innerTableTestId + rowIndex + cellIndex}" ${dataTypeString} colSpan="${cells.colSpan || 1}" rowSpan="${cells.rowSpan || 1}" style="width: ${cellWidth}px"><div contenteditable="false">${sanitizeCellContent(cells.content)}</div></td>`
						} else {
							let heightTmp = height[rowIndex] || 40
							const rowSpan = cells.rowSpan || 1

							if (rowSpan > 1) {
								for (let i = 1; i < rowSpan; i++) {
									heightTmp += height[rowIndex + i] || 40
								}
							}

							tableContainer += `<td id="prefix-${cells.cellUID || cells.cellUid}" data-testid="${innerTableTestId + rowIndex + cellIndex}" ${dataTypeString} colSpan="${cells.colSpan || 1}" rowSpan="${cells.rowSpan || 1}" style="width: ${cellWidth}px"><div contenteditable="${isWrite.value || false}" style="height: ${heightTmp}px">${sanitizeCellContent(cells.content)}</div></td>`
						}
					}
				})
			} else {
				tableContainer += `<td class="number--line" style="height: ${height[rowIndex]}px; box-sizing: content-box;" data-testid="${columnNumberlineTestId + rowIndex}">${String(rowIndex + 1)}</td>`
			}
			tableContainer += `</tr>`
		})

		// 병합 셀의 rowSpan이 실제 행 수보다 클 경우 부족한 행을 보충
		let maxRequiredRows = rows.length
		rows.forEach((row: any, rowIndex: number) => {
			if (row && row.length > 0) {
				row.forEach((cell: any) => {
					const rs = parseInt(cell.rowSpan || cell.dataType?.rowSpan) || 1
					const required = rowIndex + rs
					if (required > maxRequiredRows) {
						maxRequiredRows = required
					}
				})
			}
		})
		for (let i = rows.length; i < maxRequiredRows; i++) {
			tableContainer += `<tr>`
			tableContainer += `<td class="number--line" style="height: ${height[i] || 40}px" data-testid="${columnNumberlineTestId + i}">${String(i + 1)}</td>`
			tableContainer += `</tr>`
		}

		tableContainer += `</table>`
		tableContainer += `<div class="table--selector__list"></div></div>`

		return tableContainer
	}

	async function event(targetBlock?: HTMLElement | null) {
		const { keydownEvent, keyupEvent, inputEvent, pasteEvent, copyEvent, cutEvent, focusEvent, focusOutEvent, tableMouseDownEvent, selectorMouseDownEvent, finishFunction } = usedTable
		const { isWrite } = storeToRefs(useEditorStore())
		if (!isWrite.value) return

		const editorBlockTableList = targetBlock && targetBlock.dataset.type === 'defaultTable' ? [targetBlock] : Array.from(document.querySelectorAll('.editor--block[data-type="defaultTable"]'))

		if (editorBlockTableList && editorBlockTableList.length > 0) {
			for (const editorBlockTable of editorBlockTableList) {
				const table: any = editorBlockTable.querySelector('table')
				await nextTick()
				if (table) {
					const hasWeakSet = eventsAttachedTables.has(editorBlockTable as HTMLElement)
					const hasDataset = (editorBlockTable as HTMLElement).dataset.eventsAttached === 'true'

					// WeakSet과 dataset 이중 체크로 중복 방지 강화
					if (hasWeakSet || hasDataset) {
						continue
					}

					// 이벤트 위임: 테이블 단위로 한 번만 리스너 등록
					editorBlockTable.removeEventListener('keydown', keydownEvent)
					editorBlockTable.removeEventListener('keyup', keyupEvent)
					editorBlockTable.removeEventListener('input', inputEvent)
					editorBlockTable.removeEventListener('paste', pasteEvent)
					editorBlockTable.removeEventListener('copy', copyEvent)
					editorBlockTable.removeEventListener('cut', cutEvent)
					editorBlockTable.removeEventListener('focusin', focusEvent)
					editorBlockTable.removeEventListener('focusout', focusOutEvent)
					editorBlockTable.removeEventListener('mousedown', tableMouseDownEvent)
					editorBlockTable.removeEventListener('click', tableClickDelegate)
					editorBlockTable.removeEventListener('contextmenu', contextMenuEvent)

					editorBlockTable.addEventListener('keydown', keydownEvent)
					editorBlockTable.addEventListener('keyup', keyupEvent)
					editorBlockTable.addEventListener('input', inputEvent)
					editorBlockTable.addEventListener('paste', pasteEvent)
					editorBlockTable.addEventListener('copy', copyEvent)
					editorBlockTable.addEventListener('cut', cutEvent)
					editorBlockTable.addEventListener('focusin', focusEvent)
					editorBlockTable.addEventListener('focusout', focusOutEvent)
					editorBlockTable.addEventListener('mousedown', tableMouseDownEvent)
					editorBlockTable.addEventListener('click', tableClickDelegate)

					editorBlockTable.addEventListener('contextmenu', contextMenuEvent)
					const blockChild = [...editorBlockTable.querySelectorAll('div[contenteditable="true"]')]

					blockChild.forEach((child: any) => {
						child.removeEventListener('keyup', eventDefault)
					})

					eventsAttachedTables.add(editorBlockTable as HTMLElement)
					;(editorBlockTable as HTMLElement).dataset.eventsAttached = 'true'
				}
			}
		}

		// 전역 클릭 이벤트: 테이블 외부 클릭 시 수식 종료
		if (!globalClickHandlerAttached) {
			globalClickHandler = (event: any) => {
				const { isFunctionActive, functionTarget } = storeToRefs(useEditorStore())

				if (!isFunctionActive.value || !functionTarget.value) return

				const target = event.target as HTMLElement
				const clickedTable = target.closest('.editor--block[data-type="defaultTable"]')
				const activeTable = functionTarget.value.closest('.editor--block[data-type="defaultTable"]')

				// 클릭한 곳이 현재 활성화된 테이블이 아니면 수식 종료
				if (clickedTable !== activeTable) {
					finishFunction()
				}
			}

			document.addEventListener('click', globalClickHandler, true)
			globalClickHandlerAttached = true
		}

		tableInstanceCount++

		// newBlockEvent()
	}
	function contextMenuEvent(event: any) {
		event.preventDefault()

		const target = event.target as HTMLElement
		if (target.closest('.number--line')) {
			return
		}

		const { open } = useContextMenu()
		open(event)
	}

	// 테이블 단위 클릭 위임 핸들러(항상 동일 참조 유지)
	function tableClickDelegate(event: any) {
		const { isFunctionActive } = storeToRefs(useEditorStore())

		// 이미 함수 편집 모드면 무시
		if (isFunctionActive.value) {
			return
		}

		const target: any = event.target as HTMLElement
		const block = target.closest('.editor--block__container')
		block.style.zIndex = '100'
		if (!target) return
		// 드롭다운 바깥 클릭 시 닫기
		const selectorList: any = block.querySelector('.table--selector__list') as HTMLElement
		if (selectorList && selectorList.style.display === 'block') {
			const isClickOnList = !!target.closest('.table--selector__list')
			const isClickOnDropbox = !!target.closest('.table--dropbox')
			if (!isClickOnList && !isClickOnDropbox) {
				selectorList.style.display = 'none'
				block.style.zIndex = '1'
				// 바깥 클릭으로 닫혔으면 추가 처리 없이 종료
				return
			}
		}
		// 셀 삭제 아이콘
		if (target.closest('.ico--table__close')) {
			usedTable.clearCell(event)
			return
		}
		// PDF 셀 클릭 처리
		const pdfCell = target.closest('td[data-type="pdf"]')
		if (pdfCell && !target.closest('.ico--table__close')) {
			const { isPdfOpen, pdfUrl, pdfName } = storeToRefs(useEditorStore())
			isPdfOpen.value = true
			pdfUrl.value = pdfCell.dataset.pdfUrl || pdfCell.dataset.pdfurl || ''
			pdfName.value = pdfCell.dataset.pdfName || pdfCell.dataset.pdfname || 'file.pdf'
			return
		}
		// 드롭박스 셀 클릭 처리
		if (target.closest('.table--dropbox')) {
			usedTable.tableOptionEvent(event)
			return
		}

		// 셀 클릭 시 focus 처리 (selector는 focusEvent가 처리)
		const clickedCell = target.closest('td:not(.number--line)')
		if (clickedCell) {
			const cellDiv = clickedCell.querySelector('div[contenteditable]')

			if (cellDiv) {
				const { selector, cellTarget } = storeToRefs(useEditorStore())
				const editorBlock = clickedCell.closest('.editor--block')

				if (editorBlock) {
					selector.value = editorBlock

					const allBlocks = document.querySelectorAll('.editor--block')
					allBlocks.forEach((b: any) => b.classList.remove('active'))
				}

				const isEditable = cellDiv.getAttribute('contenteditable') === 'true'

				if (isEditable) {
					if (document.activeElement !== cellDiv) {
						;(cellDiv as HTMLElement).focus()
					}
				} else {
					cellTarget.value = clickedCell

					window.getSelection()?.removeAllRanges()

					const table = clickedCell.closest('table')
					if (table) {
						const createTableInstance = useCreateTable()
						createTableInstance.tableShapes(table)
						createTableInstance.selector()
						createTableInstance.widthSizeItem(table)
						createTableInstance.heightSizeItem(table)

						useSelectorOverlay().positionSelector(cellDiv)
					}
				}

				return
			}
		}

		// 테이블 블록 자체(셀 외부)를 클릭한 경우
		// 전체 블록 선택은 하지 않고, selector만 업데이트
		const editorBlock = target.closest('.editor--block[data-type="defaultTable"]')
		if (editorBlock && !clickedCell) {
			const { selector } = storeToRefs(useEditorStore())

			// 모든 블록의 active 클래스 제거
			const allBlocks = document.querySelectorAll('.editor--block')
			allBlocks.forEach((b: any) => b.classList.remove('active'))

			selector.value = editorBlock
		}
	}

	function selector() {
		const { selector } = storeToRefs(useEditorStore())
		const isSelectorOverlay = selector.value.querySelector('.table--selector__overlay')

		if (!isSelectorOverlay) {
			const selectorOverlay = document.createElement('div')
			selectorOverlay.setAttribute('contenteditable', 'false')
			selectorOverlay.setAttribute('tabIndex', '-1')
			selectorOverlay.classList.add('table--selector__overlay')
			selectorOverlay.style.visibility = 'hidden'

			const selectorDiv = document.createElement('div')
			selectorDiv.setAttribute('contenteditable', 'false')
			selectorDiv.setAttribute('tabIndex', '-1')
			selectorDiv.classList.add('table--selector')

			const selectorLeftTopPointer = document.createElement('div')
			selectorLeftTopPointer.setAttribute('contenteditable', 'false')
			selectorLeftTopPointer.setAttribute('tabIndex', '-1')
			selectorLeftTopPointer.classList.add('table--selector__pointer', 'table--selector__pointer-left-top')

			const selectorRightBottomPointer = document.createElement('div')
			selectorRightBottomPointer.setAttribute('contenteditable', 'false')
			selectorRightBottomPointer.setAttribute('tabIndex', '-1')
			selectorRightBottomPointer.classList.add('table--selector__pointer', 'table--selector__pointer-right-bottom')

			selectorOverlay.appendChild(selectorDiv)
			selectorOverlay.appendChild(selectorLeftTopPointer)
			selectorOverlay.appendChild(selectorRightBottomPointer)
			selector.value.appendChild(selectorOverlay)
		}
	}

	async function addCell(type: string) {
		const { cellTarget, tableSelector, blockList, selector } = storeToRefs(useEditorStore())

		const table: any = cellTarget.value.closest('table')

		const overCell: any = []
		let minCellNumber = -1
		let selectCellNumber = -1
		let maxCellNumber = -1
		let minRowNumber = -1
		let selectRowNumber = -1
		let maxRowNumber = -1
		tableSelector.value.forEach((rows: any, rowIndex: number) => {
			rows.forEach((cell: any, cellIndex: number) => {
				if (cell.html.id === cellTarget.value.id) {
					if (minCellNumber === -1) {
						minCellNumber = cellIndex - 1
					}

					if (selectCellNumber === -1) {
						selectCellNumber = cellIndex
					}

					if (maxCellNumber < cellIndex) {
						maxCellNumber = cellIndex + 1
					}

					if (minRowNumber === -1) {
						minRowNumber = rowIndex - 1 < 0 ? 0 : rowIndex - 1
					}

					if (selectRowNumber === -1) {
						selectRowNumber = rowIndex
					}

					if (maxRowNumber < rowIndex) {
						maxRowNumber = rowIndex + 1
					}
				}
			})
		})

		const rows: any = []
		const cellOver: any = []

		switch (type) {
			case 'left':
			case 'right':
				tableSelector.value.forEach((rowList: any, rowIndex: number) => {
					rowList.forEach((cell: any, colIndex: number) => {
						if (colIndex === selectCellNumber) {
							const beforeCellNumber: number = type === 'left' ? minCellNumber : selectCellNumber
							const nextCellNumber: number = type === 'left' ? selectCellNumber : maxCellNumber

							const beforeCell: any = tableSelector.value[rowIndex][beforeCellNumber].html
							const selectCell: any = tableSelector.value[rowIndex][nextCellNumber].html

							const newCell = document.createElement('td')

							if (beforeCell.id === selectCell.id && !beforeCell.classList.contains('number--line') && !cellOver.includes(cell.html.id)) {
								newCell.id = beforeCell.id
								beforeCell.colSpan += 1
								newCell.innerHTML = beforeCell.innerHTML
							} else {
								let tmpCellHeader: boolean = false
								tableSelector.value[rowIndex].forEach((tmpCell: any, tmpCellIndex: number) => {
									if (tmpCell.html.dataset.header === 'true') {
										tmpCellHeader = true
									}
								})

								newCell.id = useCommonStore().uniqueId()
								if (tmpCellHeader) {
									newCell.dataset.header = 'true'
								}
								const newDiv = document.createElement('div')
								newDiv.setAttribute('contenteditable', 'true')
								newCell.appendChild(newDiv)
							}

							const data = {
								html: newCell,
							}

							tableSelector.value[rowIndex].splice(colIndex + (type === 'left' ? 0 : 1), 0, data)
							cellOver.push(cell.html.id)
						}
					})
				})
				break

			case 'up':
			case 'down':
				tableSelector.value.forEach((rowList: any, rowIndex: number) => {
					if (rowIndex === selectRowNumber) {
						const rowData: any = []
						const rowNumber: number = type === 'up' ? selectRowNumber : maxRowNumber
						const newRow = table.insertRow(rowNumber)
						rowList.forEach((cell: any, colIndex: number) => {
							const beforeRowNumber: number = type === 'up' ? minRowNumber : selectRowNumber
							const nextRowNumber: number = type === 'up' ? selectRowNumber : maxRowNumber

							const beforeCell: any = tableSelector.value[beforeRowNumber][colIndex].html
							const selectCell: any = tableSelector.value[nextRowNumber][colIndex].html

							const newCell = document.createElement('td')

							if (beforeCell === selectCell && !beforeCell.classList.contains('number--line') && !cellOver.includes(cell.html.id)) {
								newCell.id = beforeCell.id
								beforeCell.rowSpan += 1
								const newDiv = document.createElement('div')
								newDiv.setAttribute('contenteditable', 'true')
								newCell.appendChild(newDiv)
							} else {
								newCell.id = useCommonStore().uniqueId()
								const newDiv = document.createElement('div')
								newDiv.setAttribute('contenteditable', 'true')
								newCell.appendChild(newDiv)
							}

							newRow.appendChild(newCell)

							const data = {
								html: newCell,
							}

							rowData.push(data)
							cellOver.push(cell.html.id)
						})

						tableSelector.value.splice(rowIndex + (type === 'up' ? 0 : 1), 0, rowData)
					}
				})
				break
		}

		tableSelector.value.forEach((rowList: any, rowIndex: number) => {
			if (rowIndex !== 0) {
				rows[rowIndex - 1] = []
				rowList.forEach((cell: any, cellIndex: number) => {
					if (cellIndex !== 0) {
						if (!overCell.includes(cell.html.id)) {
							const dataType: any = setDataType(cell.html)

							let addCellContent: string
							if (cell.html.dataset.type === 'function') {
								addCellContent = cell.html.querySelector('span')?.innerHTML ?? ''
							} else if (cell.html.dataset.type === 'image') {
								addCellContent = cell.html.querySelector('img')?.outerHTML ?? ''
							} else {
								addCellContent = cell.html.querySelector('div')?.innerHTML ?? ''
							}

							rows[rowIndex - 1].push({
								cellUid: cell.html.id.includes('temp') ? '' : deletePrefix(cell.html.id),
								colSpan: cell.html.colSpan,
								rowSpan: cell.html.rowSpan,
								content: addCellContent,
								dataType: dataType,
							})

							overCell.push(cell.html.id)
						}
					}
				})
			}
		})

		const blockUid = selector.value.id.includes('temp') ? '' : deletePrefix(selector.value.id)

		// 현재 table의 ID를 보존
		const block: any = document.getElementById(selector.value.id) as HTMLElement
		const currentTable = block?.querySelector('table')
		const currentTableId = currentTable?.id ? currentTable.id.replace('prefix-', '') : ''

		let blockContent: any
		blockList.value.forEach((item: any) => {
			if (item.blockUid === blockUid) {
				item.content.cell = rows
				if (currentTableId) {
					item.content.tableUID = currentTableId
				}
				blockContent = item.content
			}
		})

		if (block) {
			block.innerHTML = useCreateTable().insertTable(blockContent, blockUid)
		}
		// socketSave() 제거: useAddCell.ts에서 이미 createTableBlock을 직접 호출하므로 중복 저장 방지
		// 이 함수는 더 이상 사용되지 않으며, useAddCell.ts의 addCell이 실제 사용됨
		new UseHistory().push()
	}

	function tableShapes(table: HTMLTableElement) {
		// thead 행은 제외하고 tbody 행만 가져오기
		const tbody = table.querySelector('tbody')
		let allRows: HTMLTableRowElement[]

		if (tbody) {
			// tbody가 있으면 tbody의 tr만 선택하되, 첫 번째 행(헤더)은 제외
			const allTbodyRows = [...tbody.querySelectorAll('tr')]
			allRows = allTbodyRows.slice(1) // 첫 번째 행(헤더) 제외
		} else {
			// tbody가 없으면 모든 tr을 가져오되, 첫 번째 tr (헤더)는 제외
			const allTrs = [...table.querySelectorAll('tr')] as HTMLTableRowElement[]
			allRows = allTrs.slice(1) // 첫 번째 행(헤더) 제외
		}

		// tbody의 모든 행 포함 (병합 셀로 인해 td가 없는 행도 포함)
		const rowList = allRows

		// thead의 number-line 개수로 maxColumnCount 계산 (가장 정확)
		const headerRow = table.querySelector('tr')
		const headerNumberLines = headerRow ? [...headerRow.querySelectorAll('td.number--line')] : []
		let maxColumnCount = Math.max(0, headerNumberLines.length - 1) // 첫 번째 number-line 제외

		// 혹시 모를 경우를 대비해 실제 셀로도 확인
		if (maxColumnCount === 0) {
			rowList.forEach((row: any) => {
				const cells = [...row.children].filter((cell: any) => !cell.classList.contains('number--line'))
				let columnCount = 0
				cells.forEach((cell: any) => {
					columnCount += cell.colSpan || 1
				})
				if (columnCount > maxColumnCount) {
					maxColumnCount = columnCount
				}
			})
		}

		const tableArray = Array.from({ length: rowList.length }, () => Array.from({ length: maxColumnCount }, () => ({ html: '' })))

		rowList.forEach((rows: any) => {
			const cellList = [...rows.getElementsByTagName('td')]
			cellList.forEach((cell: any) => {
				if (cell.style.display === 'none') {
					cell.remove()
				}
			})
		})

		rowList.forEach((rows: any, rowIndex: number) => {
			const cellList = [...rows.getElementsByTagName('td')].filter((cell: any) => !cell.classList.contains('number--line'))
			// 테이블 배열 생성
			let cellCnt = 0 // 테이블 배열 인덱스
			let tmpRowCnt = rowIndex // 테이블 배열 행 인덱스
			let cellTmpCnt = -1 // 테이블 배열 열 인덱스
			cellList.forEach((cell: any, colIndex: number) => {
				while (tableArray?.[tmpRowCnt]?.[cellCnt]?.html) {
					cellCnt++
				}

				if (!tableArray?.[tmpRowCnt]?.[cellCnt]) {
					if (cellTmpCnt === -1) {
						cellTmpCnt = colIndex
					}
					return
				}

				if (cellCnt > tableArray[tmpRowCnt].length - 1) {
					cellCnt = tableArray[tmpRowCnt].length - 1
				}
				tableArray[tmpRowCnt][cellCnt] = { html: cell }
				if (cell.rowSpan > 1 && cell.colSpan > 1) {
					for (let i = cell.rowSpan - 1; i >= 0; i--) {
						let tmpCellCnt = cellCnt
						for (let j = cell.colSpan - 1; j >= 0; j--) {
							if (tableArray[tmpRowCnt] && tableArray[tmpRowCnt][tmpCellCnt] !== undefined) {
								tableArray[tmpRowCnt][tmpCellCnt] = { html: cell }
							}
							tmpCellCnt++
						}
						tmpRowCnt++
					}
				} else if (cell.rowSpan > 1) {
					for (let i = cell.rowSpan - 1; i >= 0; i--) {
						if (tableArray[tmpRowCnt] && tableArray[tmpRowCnt][cellCnt] !== undefined) {
							tableArray[tmpRowCnt][cellCnt] = { html: cell }
						}
						tmpRowCnt++
					}
				} else if (cell.colSpan > 1) {
					let tmpCellCnt = cellCnt
					for (let i = cell.colSpan - 1; i >= 0; i--) {
						tableArray[tmpRowCnt][tmpCellCnt] = { html: cell }
						tmpCellCnt++
					}
				}
				tmpRowCnt = rowIndex
				cellCnt++
			})
		})

		const { tableSelector } = storeToRefs(useEditorStore())

		tableSelector.value = tableArray
	}

	function addFunctionList() {
		const { filterList, selector, cellTarget, isFunctionList, functionListTimeout, enterSelector } = storeToRefs(useEditorStore())
		isFunctionList.value = true
		const functionListContainer: HTMLDivElement | any = document.createElement('DIV')
		functionListContainer.classList.add('table--function__list')

		functionItemAdd(functionListContainer, filterList.value)

		selector.value.appendChild(functionListContainer)
		const cellUid = cellTarget.value.id
		const cell = document.querySelector(`#${cellUid}`) as HTMLTableCellElement
		const rect = cell.getBoundingClientRect()
		const parentBlock: any = cell.closest('.editor--block')
		const parentRect: any = parentBlock?.getBoundingClientRect()

		const width = rect.width < 150 ? 150 : rect.width
		functionListContainer.style.left = rect.left - parentRect.left - (width - rect.width) / 2 + 'px'
		functionListContainer.style.top = rect.top - parentRect.top + rect.height + 'px'
		functionListContainer.style.width = width + 1 + 'px'

		const useDelete = useDeleteTable()
		enterSelector.value.style.zIndex = '9999'
		enterSelector.value.parentElement.style.zIndex = '9999'

		const closeHandler = (e: any) => {
			const clickedElement = e.target as HTMLElement
			const functionList = clickedElement.closest('.table--function__list')

			if (!functionList) {
				useDelete.functionList()
				document.removeEventListener('click', closeHandler)
			}
		}
		document.addEventListener('click', closeHandler)
		clearTimeout(functionListTimeout.value)
	}

	function searchFunctionList(text: string) {
		let functionListContainer = document.querySelector('.table--function__list') as HTMLDivElement
		if (functionListContainer === null) {
			addFunctionList()
		}

		const useSelection = new UseSelection()
		useSelection.setChangeSelection()

		functionListContainer = document.querySelector('.table--function__list') as HTMLDivElement
		const { filterList } = storeToRefs(useEditorStore())

		functionListContainer.innerHTML = ''

		const filter = filterList.value.filter((item: any) => {
			const textLower = text.toLowerCase()
			const lower = item.toLowerCase()
			return lower.includes(textLower)
		})

		functionItemAdd(functionListContainer, filter)
	}

	function functionItemAdd(container: HTMLDivElement, list: any) {
		const { cellTarget } = storeToRefs(useEditorStore())

		list.forEach((item: any) => {
			const functionItem = document.createElement('DIV')
			functionItem.classList.add('table--function__item')
			functionItem.setAttribute('title', item)
			functionItem.dataset.item = item

			const functionItemIcon = document.createElement('i')
			functionItemIcon.classList.add('ico', 'ico--table__function')

			const functionItemText = document.createElement('SPAN')
			functionItemText.innerHTML = item

			functionItem.appendChild(functionItemIcon)
			functionItem.appendChild(functionItemText)

			functionItem.addEventListener('mousedown', (e: any) => {
				e.stopPropagation()
				e.preventDefault()
			})

			functionItem.addEventListener('click', (e: any) => {
				e.stopPropagation()
				const target = cellTarget.value.querySelector('div[contenteditable="true"]')
				functionItemEvent(target, item)
			})

			container.appendChild(functionItem)
		})
	}

	function functionItemEvent(target: any, item: string) {
		const { isFunctionActive, isFunctionList, functionTarget, cellTarget, functionListTimeout } = storeToRefs(useEditorStore())
		const cellUid = cellTarget.value.id
		const cell = document.querySelector(`#${cellUid}`) as HTMLTableCellElement

		functionTarget.value = cell
		cell.dataset.type = 'function'
		cell.dataset.function = item
		const cellSpan: any = cell.querySelector('div')
		cellSpan.innerHTML = `=${item}()`
		const range = document.createRange()

		const textLength = cellSpan.textContent.length

		range.setStart(cellSpan.firstChild, textLength - 1)
		range.collapse(true)

		const useDelete = useDeleteTable()

		clearTimeout(functionListTimeout.value)
		isFunctionActive.value = true
		useDelete.functionList(true)
		isFunctionList.value = false

		const selection = window.getSelection()

		if (selection) {
			selection.removeAllRanges()
			selection.addRange(range)
		}
	}

	function widthSizeItem(targetTable?: HTMLElement) {
		const { tableSelector } = storeToRefs(useEditorStore())

		const overCell: any = []
		tableSelector.value.forEach((row: any) => {
			row.forEach((cell: any) => {
				if (cell && cell.html) {
					if (!cell.html.classList.contains('number--line') && !overCell.includes(cell.html.id)) {
						let existingSpan = cell.html.querySelector('.table--selector__width')
						if (!existingSpan) {
							const newSpan = document.createElement('SPAN')
							newSpan.classList.add('table--selector__width')
							newSpan.dataset.cellId = cell.html.id
							cell.html.appendChild(newSpan)
						}
						overCell.push(cell.html.id)
					}
				}
			})
		})

		widthSizeHover(targetTable)
	}

	function widthSizeHover(targetTable?: HTMLElement) {
		const { tableSelector } = storeToRefs(useEditorStore())
		const selector = targetTable || document
		const tableSelectorWidth: any = selector.querySelectorAll('.table--selector__width')

		tableSelectorWidth.forEach((width: any) => {
			width.addEventListener('mouseenter', (e: any) => {
				const cell = e.target.closest('td')
				const table = cell.closest('table')

				// tableSelector가 다른 테이블을 가리키면 현재 테이블로 갱신
				const cellInSelector = tableSelector.value.some((row: any) => row.some((c: any) => c && c.html === cell))
				if (!cellInSelector && table) {
					tableShapes(table)
				}

				// tableSelector에서 현재 셀의 논리적 열 위치 찾기
				let targetColumnIndex = -1

				// 현재 hover된 셀이 속한 테이블 내에서 index를 찾아야 함
				// findIndex를 사용하여 첫 번째(Origin) 열 인덱스를 찾음
				tableSelector.value.some((row: any) => {
					const colIndex = row.findIndex((c: any) => c && c.html === cell)
					if (colIndex !== -1) {
						targetColumnIndex = colIndex + cell.colSpan - 1
						return true
					}
					return false
				})

				if (targetColumnIndex === -1) return

				// 같은 논리적 열 인덱스를 가진 모든 셀을 찾아서 활성화
				const processedCells = new Set()
				tableSelector.value.forEach((row: any) => {
					row.forEach((cells: any, colIndex: any) => {
						if (!cells || !cells.html) return
						if (cells.html.classList.contains('number--line')) return

						// 같은 테이블 내의 셀인지 확인
						if (cells.html.closest('table') !== table) return

						// 이미 처리한 셀은 건너뛰기
						if (processedCells.has(cells.html.id)) return

						// 이 셀의 '실제' 시작 열 인덱스를 찾음
						let originColIndex = colIndex
						while (originColIndex > 0 && row[originColIndex - 1]?.html === cells.html) {
							originColIndex--
						}

						// 시작 열 인덱스 + colSpan - 1이 타겟 인덱스와 같으면 활성화
						const cellFinalColumnIndex = originColIndex + cells.html.colSpan - 1

						if (cellFinalColumnIndex === targetColumnIndex) {
							const widthSelector = cells.html.querySelector('.table--selector__width')
							if (widthSelector) {
								widthSelector.classList.add('active')
								processedCells.add(cells.html.id)
							}
						}
					})
				})
			})
			width.addEventListener('mouseleave', (e: any) => {
				const cell = e.target.closest('td')
				const table = cell.closest('table')

				const cellInSelectorLeave = tableSelector.value.some((row: any) => row.some((c: any) => c && c.html === cell))
				if (!cellInSelectorLeave && table) {
					tableShapes(table)
				}

				// tableSelector에서 현재 셀의 논리적 열 위치 찾기
				let targetColumnIndex = -1
				tableSelector.value.some((row: any) => {
					const colIndex = row.findIndex((c: any) => c && c.html === cell)
					if (colIndex !== -1) {
						targetColumnIndex = colIndex + cell.colSpan - 1
						return true
					}
					return false
				})

				if (targetColumnIndex === -1) return

				// 같은 논리적 열 인덱스를 가진 모든 셀을 찾아서 비활성화
				const processedCells = new Set()
				tableSelector.value.forEach((row: any) => {
					row.forEach((cells: any, colIndex: any) => {
						if (!cells || !cells.html) return
						if (cells.html.classList.contains('number--line')) return

						// 같은 테이블 내의 셀인지 확인
						if (cells.html.closest('table') !== table) return

						if (processedCells.has(cells.html.id)) return

						// 이 셀의 '실제' 시작 열 인덱스를 찾음
						let originColIndex = colIndex
						while (originColIndex > 0 && row[originColIndex - 1]?.html === cells.html) {
							originColIndex--
						}

						const cellFinalColumnIndex = originColIndex + cells.html.colSpan - 1

						if (cellFinalColumnIndex === targetColumnIndex) {
							const widthSelector = cells.html.querySelector('.table--selector__width')
							if (widthSelector) {
								widthSelector.classList.remove('active')
								processedCells.add(cells.html.id)
							}
						}
					})
				})
			})
			width.addEventListener('mousedown', useSize().tableWidthMouseDown)
		})
	}

	function heightSizeItem(targetTable?: HTMLElement) {
		const { tableSelector } = storeToRefs(useEditorStore())

		const overCell: any = []
		tableSelector.value.forEach((row: any) => {
			row.forEach((cell: any) => {
				if (cell && cell.html) {
					if (!cell.html.classList.contains('number--line') && !overCell.includes(cell.html.id)) {
						let existingSpan = cell.html.querySelector('.table--selector__height')
						if (!existingSpan) {
							const newSpan = document.createElement('SPAN')
							newSpan.classList.add('table--selector__height')
							newSpan.dataset.cellId = cell.html.id
							cell.html.appendChild(newSpan)
						}
						overCell.push(cell.html.id)
					}
				}
			})
		})

		heightSizeHover(targetTable)
	}

	function heightSizeHover(targetTable?: HTMLElement) {
		const { tableSelector } = storeToRefs(useEditorStore())
		const selector = targetTable || document
		const tableSelectorHeight: any = selector.querySelectorAll('.table--selector__height')

		tableSelectorHeight.forEach((height: any) => {
			height.addEventListener('mouseenter', (e: any) => {
				const cell = e.target.closest('td')
				const table = cell.closest('table')

				// tableSelector가 다른 테이블을 가리키면 현재 테이블로 갱신
				const cellInSelector = tableSelector.value.some((row: any) => row.some((c: any) => c && c.html === cell))
				if (!cellInSelector && table) {
					tableShapes(table)
				}

				// tableSelector에서 현재 셀의 논리적 행 위치 찾기
				let targetRowIndex = -1
				// findIndex를 사용하여 첫 번째(Origin) 행 인덱스를 찾음
				targetRowIndex = tableSelector.value.findIndex((row: any) => row.some((c: any) => c && c.html === cell))

				if (targetRowIndex !== -1) {
					targetRowIndex = targetRowIndex + cell.rowSpan - 1
				}

				if (targetRowIndex === -1) return

				// 같은 논리적 행 인덱스를 가진 모든 셀을 찾아서 활성화
				const processedCells = new Set()
				tableSelector.value.forEach((row: any, rowIndex: any) => {
					row.forEach((cells: any, colIndex: any) => {
						if (!cells || !cells.html) return
						if (cells.html.classList.contains('number--line')) return

						// 같은 테이블 내의 셀인지 확인
						if (cells.html.closest('table') !== table) return

						// 이미 처리한 셀은 건너뛰기
						if (processedCells.has(cells.html.id)) return

						// 이 셀의 '실제' 시작 행 인덱스를 찾음
						let originRowIndex = rowIndex
						while (originRowIndex > 0 && tableSelector.value[originRowIndex - 1][colIndex]?.html === cells.html) {
							originRowIndex--
						}

						// 시작 행 인덱스 + rowSpan - 1이 타겟 인덱스와 같으면 활성화
						const cellFinalRowIndex = originRowIndex + cells.html.rowSpan - 1

						if (cellFinalRowIndex === targetRowIndex) {
							const heightSelector = cells.html.querySelector('.table--selector__height')
							if (heightSelector) {
								heightSelector.classList.add('active')
								processedCells.add(cells.html.id)
							}
						}
					})
				})
			})
			height.addEventListener('mouseleave', (e: any) => {
				const cell = e.target.closest('td')
				const table = cell.closest('table')

				const cellInSelectorLeave = tableSelector.value.some((row: any) => row.some((c: any) => c && c.html === cell))
				if (!cellInSelectorLeave && table) {
					tableShapes(table)
				}

				// tableSelector에서 현재 셀의 논리적 행 위치 찾기
				let targetRowIndex = -1
				targetRowIndex = tableSelector.value.findIndex((row: any) => row.some((c: any) => c && c.html === cell))

				if (targetRowIndex !== -1) {
					targetRowIndex = targetRowIndex + cell.rowSpan - 1
				}

				if (targetRowIndex === -1) return

				// 같은 논리적 행 인덱스를 가진 모든 셀을 찾아서 비활성화
				const processedCells = new Set()
				tableSelector.value.forEach((row: any, rowIndex: any) => {
					row.forEach((cells: any, colIndex: any) => {
						if (!cells || !cells.html) return
						if (cells.html.classList.contains('number--line')) return

						// 같은 테이블 내의 셀인지 확인
						if (cells.html.closest('table') !== table) return

						if (processedCells.has(cells.html.id)) return

						// 이 셀의 '실제' 시작 행 인덱스를 찾음
						let originRowIndex = rowIndex
						while (originRowIndex > 0 && tableSelector.value[originRowIndex - 1][colIndex]?.html === cells.html) {
							originRowIndex--
						}

						const cellFinalRowIndex = originRowIndex + cells.html.rowSpan - 1

						if (cellFinalRowIndex === targetRowIndex) {
							const heightSelector = cells.html.querySelector('.table--selector__height')
							if (heightSelector) {
								heightSelector.classList.remove('active')
								processedCells.add(cells.html.id)
							}
						}
					})
				})
			})
			height.addEventListener('mousedown', useSize().tableHeightMouseDown)
		})
	}

	function clearEventFlags(element: HTMLElement) {
		eventsAttachedTables.delete(element)
		delete element.dataset.eventsAttached
	}

	function cleanupGlobalEventListeners() {
		tableInstanceCount--
		if (tableInstanceCount <= 0 && globalClickHandler) {
			document.removeEventListener('click', globalClickHandler, true)
			globalClickHandler = null
			globalClickHandlerAttached = false
			tableInstanceCount = 0
		}
	}

	return {
		table,
		event,
		selector,
		addCell,
		tableShapes,
		addFunctionList,
		searchFunctionList,
		widthSizeItem,
		heightSizeItem,
		insertTable,
		clearEventFlags,
		cleanupGlobalEventListeners,
	}
}
